import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { formatCurrency, formatDate } from 'utils/format';
import FormattedAddress from 'components/shared/address/formatted-address.component';
import { LabOrderInput } from 'models/order-input/lab-order';
import { CurrencyContext } from 'context/currency';

type DeliverySummaryProps = {
    order: LabOrderInput;
};
const DeliverySummary: FC<DeliverySummaryProps> = ({ order }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { currency } = useContext(CurrencyContext);
    const [price, setPrice] = useState(0);
    const time = order.deliveryDetails?.time;
    const internalDeliveryDate = order.internalDeliveryDate;
    const externalDeliveryDate = order.externalDeliveryDate;

    useEffect(() => {
        let totalPrice = 0;
        order.constructions.forEach((construction) => {
            if (!construction.warranty) {
                const actionPrices = construction.actions.reduce((total, actions) => {
                    const priceWithVat = actions.priceWithVat || 0;
                    return total + priceWithVat;
                }, 0);
                const materialPrices = construction.materials.reduce((total, material) => {
                    const priceWithVat = material.priceWithVat || 0;
                    return total + priceWithVat;
                }, 0);
                totalPrice += actionPrices + materialPrices;
            }
        });
        setPrice(totalPrice);
    }, [order.constructions]);

    return (
        <Grid container>
            <Grid item xs={12} md={3}>
                <Box mb={2}>
                    <Box mb={0.25}>
                        <Typography variant="subtitle2">{translate('common:PRICE')}</Typography>
                    </Box>
                    <Box>{formatCurrency(price, currency)}</Box>
                </Box>
                <Box>
                    <Box mb={0.25}>
                        <Typography variant="subtitle2">{translate('orders:DELIVERY_DATE')}</Typography>
                    </Box>
                    <Box display="flex">
                        <Box>{formatDate(order.deliveryDetails.date || new Date(), 'date')}</Box>
                        {time?.hour && time?.hour !== 0 ? (
                            <Box ml={1}>
                                {time.hour <= 9 ? '0' + time.hour : time.hour}:
                                {time?.minute ? (time.minute <= 9 ? '0' + time.minute : time.minute) : '00'}
                            </Box>
                        ) : undefined}
                    </Box>
                </Box>
            </Grid>

            <Grid item xs={12} md={3}>
                <Box mb={2}>
                    <Box mb={0.25}>
                        <Typography variant="subtitle2">{translate('orders:INTERNAL_DELIVERY_DATE')}</Typography>
                    </Box>
                    <Box display="flex">
                        {internalDeliveryDate ? (
                            <Box>{formatDate(internalDeliveryDate, 'date')}</Box>
                        ) : (
                            <Box>{translate('orders:DATE_UNKNOWN')}</Box>
                        )}
                    </Box>
                </Box>
                <Box>
                    <Box mb={0.25}>
                        <Typography variant="subtitle2">{translate('orders:EXTERNAL_DELIVERY_DATE')}</Typography>
                    </Box>
                    <Box display="flex">
                        {externalDeliveryDate ? (
                            <Box>{formatDate(externalDeliveryDate, 'date')}</Box>
                        ) : (
                            <Box>{translate('orders:DATE_UNKNOWN')}</Box>
                        )}
                    </Box>
                </Box>
            </Grid>

            <Grid item xs={12} md={3}>
                <Box mb={0.25}>
                    <Typography variant="subtitle2">{translate('orders:PICKUP_LOCATION')}</Typography>
                </Box>
                <Box>
                    <Box>{order.deliveryDetails.pickUpLocation?.name}</Box>
                    <Box>
                        <FormattedAddress
                            hideCountry
                            address={order.deliveryDetails.pickUpLocation?.contact?.address}
                        />
                    </Box>
                </Box>
            </Grid>

            <Grid item xs={12} md={3}>
                <Box mb={0.25}>
                    <Typography variant="subtitle2">{translate('orders:DELIVERY_DESTINATION')}</Typography>
                </Box>
                <Box>
                    <Box>{order.deliveryDetails.destination?.name}</Box>
                    <Box>
                        <FormattedAddress hideCountry address={order.deliveryDetails.destination?.contact?.address} />
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
};

export default DeliverySummary;
